import { z } from "zod";

import type { QueryTarget } from "@/shared/domain/query";

import type { TableLayout } from "./layoutDrafts";

/**
 * Bumped whenever the stored shape changes. An entry under any other version
 * reads as absent: storage outlives the build that wrote it, and a deploy
 * mid-session must not hand the new client a shape it does not understand.
 */
export const VIEW_LAYOUT_VERSION = 1;

const KEY_PREFIX = "roboto.view-layout";

/**
 * Where one person's unsaved layout on one table lives: keyed by user, org,
 * table and View, with the bare table under `"bare"`. The user is in the key
 * for the reason it is in the boards draft key: another person signed in on
 * the same browser must not see these.
 */
export function layoutKey(
  userId: string,
  orgId: string,
  target: QueryTarget,
  viewId: string | null,
): string {
  return `${KEY_PREFIX}.${userId}.${orgId}.${target}.${viewId ?? "bare"}`;
}

const entrySchema = z.object({
  version: z.literal(VIEW_LAYOUT_VERSION),
  visibleColumns: z.array(z.string()),
  sort: z
    .object({ columnId: z.string(), direction: z.enum(["asc", "desc"]) })
    .nullable(),
  pageSize: z.number().int().positive(),
});

/**
 * The layout stored under `key`, or `null` when there is none — or when what
 * is there cannot be trusted. Storage is a trust boundary: it can hold a shape
 * an older build wrote, or nothing this build has ever seen, and either has to
 * read as "no draft" rather than as malformed state seeded into a table.
 */
export function readLayout(storage: Storage, key: string): TableLayout | null {
  try {
    return parseLayout(storage.getItem(key));
  } catch {
    return null;
  }
}

/** {@link readLayout} on a raw entry already read from storage. */
export function parseLayout(raw: string | null): TableLayout | null {
  if (raw === null) {
    return null;
  }
  try {
    const parsed = entrySchema.safeParse(JSON.parse(raw));
    if (!parsed.success) {
      return null;
    }
    const { visibleColumns, sort, pageSize } = parsed.data;
    return { visibleColumns, sort, pageSize };
  } catch {
    return null;
  }
}

export function writeLayout(
  storage: Storage,
  key: string,
  layout: TableLayout,
): void {
  try {
    storage.setItem(
      key,
      JSON.stringify({ version: VIEW_LAYOUT_VERSION, ...layout }),
    );
  } catch {
    // Quota or disabled storage: the change lasts for the tab, through its
    // own memory, and no longer.
  }
  notify();
}

export function removeLayout(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // As above.
  }
  notify();
}

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Hear about every change to stored layouts: those this tab writes, and —
 * through the window's `storage` event — those another tab writes. For
 * `useSyncExternalStore`, which is how a hook keeps a layout read from storage
 * in step with it without storage being reactive on its own.
 */
export function subscribeToLayouts(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}
